import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { SelectOption } from '../../../../components/form/select-field/select-field.config';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { OptionService } from '../../../../services/api/option.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';

import {
  CompanyDocument,
  CompanyDocumentPayload,
  companyDocumentUserFullName
} from '../../../../models/entities/company-document';


@Component({
  selector: 'company-documents-form',
  imports: [ReactiveFormsModule, InputFieldComponent, SelectFieldComponent],
  templateUrl: './company-documents-form.component.html',
  styleUrl: './company-documents-form.component.scss'
})
export class CompanyDocumentsFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() companyDocument?: CompanyDocument;
  @Input() companyDocumentId?: number;
  @Input() isEditPage: boolean = false;
  @Input() readonlyForm: boolean = false;

  @Output() onSavedData = new EventEmitter<CompanyDocument>();

  form: FormGroup;
  departmentOptions: SelectOption[] = [];
  groupOptions: SelectOption[] = [];

  currentUser: any = null;
  canModify: boolean = false;

  private readonly LOADER_CONTEXT = 'company-documents-form';
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private qualityManagementService: QualityManagementService,
    private optionService: OptionService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.form = this.formBuilder.group({
      code: [{ value: '', disabled: true }],
      department: ['', [Validators.required]],
      name: ['', [Validators.required]],
      companyDocumentGroupId: [''],
      creator: [{ value: '', disabled: true }],
      currentRevision: [{ value: '', disabled: true }]
    });
  }

  ngOnInit(): void {
    this.mainLayoutService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => this.currentUser = user);

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS',
          'modify'
        );
        this.applyFieldPermissions();
      });

    this.loadDepartments();
    this.loadGroups();
    this.applyCompanyDocument();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyDocument'] || changes['readonlyForm']) {
      this.applyCompanyDocument();
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get code(): string {
    return this.companyDocument?.code ?? '';
  }

  get canSave(): boolean {
    return this.canModify && !this.readonlyForm;
  }

  get creatorLabel(): string {
    if (this.companyDocument?.creatorUser) {
      return companyDocumentUserFullName(this.companyDocument.creatorUser);
    }
    return this.currentUser && !this.companyDocument?.companyDocumentId
      ? `${this.currentUser.lastName ?? ''} ${this.currentUser.firstName ?? ''}`.trim()
      : '';
  }

  get currentRevisionCode(): string {
    return this.companyDocument?.currentRevisionCode || '-';
  }

  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }


  onDepartmentChange(department: string): void {
    if (this.isEditPage || !department) {
      return;
    }

    this.qualityManagementService.nextCompanyDocumentCode(department).subscribe({
      next: (response: any) => {
        const nextCode = this.qualityManagementService.readResponseKey(response, 'code', 'nextCode') ?? '';
        this.form.get('code')?.setValue(nextCode, { emitEvent: false });
      },
      error: (error: any) => {
        console.error('Error loading the next company document code:', error);
        this.form.get('code')?.setValue('', { emitEvent: false });
      }
    });
  }

  submitForm(): void {
    if (!this.canSave) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertNotificationService.error('Please fill in all the required fields.');
      return;
    }

    const payload = this.buildPayload();

    const request$ = this.companyDocumentId
      ? this.qualityManagementService.updateCompanyDocument(this.companyDocumentId, payload)
      : this.qualityManagementService.createCompanyDocument(payload);

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    request$.subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        const companyDocument: CompanyDocument | null =
          this.qualityManagementService.readResponseKey(response, 'companyDocument') ?? null;

        if (!companyDocument) {
          this.applyBackendErrors(response);
          return;
        }

        this.companyDocument = companyDocument;
        this.companyDocumentId = companyDocument.companyDocumentId ?? this.companyDocumentId;
        this.applyCompanyDocument();
        this.alertNotificationService.success('Company document saved.');
        this.onSavedData.emit(companyDocument);
      },
      error: (error: any) => {
        console.error('Error saving the company document:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.applyBackendErrors(error?.error, error);
      }
    });
  }

  private buildPayload(): CompanyDocumentPayload {
    const value = this.form.getRawValue();
    const groupId = value.companyDocumentGroupId;

    return {
      department: value.department,
      name: value.name,
      companyDocumentGroupId: groupId === '' || groupId === null || groupId === undefined
        ? null
        : Number(groupId)
    };
  }

  private loadDepartments(): void {
    this.optionService.companyDocumentDepartments().subscribe({
      next: (response: any) => {
        this.departmentOptions = [{ key: '', value: '-- select' }, ...(response?.data?.options ?? [])];
      },
      error: (error: any) => {
        console.error('Error loading the department options:', error);
        this.alertNotificationService.error('Failed to load the departments list.');
      }
    });
  }

  private loadGroups(): void {
    this.optionService.companyDocumentGroups().subscribe({
      next: (response: any) => {
        this.groupOptions = [{ key: '', value: '-- select' }, ...(response?.data?.options ?? [])];
      },
      error: (error: any) => {
        console.error('Error loading the group options:', error);
        this.alertNotificationService.error('Failed to load the groups list.');
      }
    });
  }

  private applyCompanyDocument(): void {
    if (this.companyDocument) {
      this.companyDocumentId = this.companyDocument.companyDocumentId ?? this.companyDocumentId;

      this.form.patchValue({
        code: this.companyDocument.code ?? '',
        department: this.companyDocument.department ?? '',
        name: this.companyDocument.name ?? '',
        companyDocumentGroupId: this.companyDocument.companyDocumentGroup?.companyDocumentGroupId ?? ''
      }, { emitEvent: false });
    }

    this.form.patchValue({
      creator: this.creatorLabel,
      currentRevision: this.currentRevisionCode
    }, { emitEvent: false });

    this.applyFieldPermissions();
  }

 
  private applyFieldPermissions(): void {
    if (!this.canSave) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
    this.form.get('code')?.disable({ emitEvent: false });
    this.form.get('creator')?.disable({ emitEvent: false });
    this.form.get('currentRevision')?.disable({ emitEvent: false });

    if (this.isEditPage) {
      this.form.get('department')?.disable({ emitEvent: false });
    }
  }

  private applyBackendErrors(response: any, error?: any): void {
    const validationErrors = response?.validationErrors ?? response?.errors ?? null;

    if (validationErrors && typeof validationErrors === 'object') {
      Object.keys(validationErrors).forEach(field => {
        const control = this.form.get(field);
        if (control) {
          control.markAsTouched();
          control.setErrors({
            ...(control.errors ?? {}),
            backendError: validationErrors[field],
            back: validationErrors[field]
          });
        }
      });

      this.alertNotificationService.error(Object.values(validationErrors).join(' '));
      return;
    }

    this.alertNotificationService.error(
      this.qualityManagementService.companyDocumentErrorMessage(
        error ?? { error: response },
        'Failed to save the company document.'
      )
    );
  }
}
